import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import * as Icons from "lucide-react";
import {
  Megaphone, Briefcase, FileText, Receipt, Users2, Search,
  BadgeCheck, MapPin, ArrowRight, ChevronDown, ChevronRight, Layers,
  MessageCircle, Sparkles, X, Building2, Send,
} from "lucide-react";
import OrgTeamPanel from "@/components/OrgTeamPanel";
import { toast } from "sonner";

/** AgencyOffice — dashboard for `account_type === "advertising_agency_admin"`.
 *
 *  Renders:
 *    · Hero (brand, legal name, verified badge, selected agency-types chips)
 *    · Always-on desks (Client Roster · Brief Locker · Delivery + Bills)
 *    · Conditional desks — union of desks unlocked by every picked agency type
 *    · Staffing panel (grouped roles, 4-tier ladders for CW + AD)
 *    · Consult module (search Casting / PR directory)
 *    · Team panel (reuses <OrgTeamPanel>)
 *
 *  Every desk is a card. Bodies are stubbed for Phase 1B — Phase 2 wires
 *  each desk to its own detail page. Data-testids everywhere for the
 *  testing agent.
 */
export default function AgencyOffice() {
  const [org, setOrg] = useState(null);
  const [meta, setMeta] = useState(null);      // /meta/agency-types
  const [roles, setRoles] = useState(null);    // /meta/agency-roles

  useEffect(() => {
    (async () => {
      const [{ data: orgResp }, { data: t }, { data: r }] = await Promise.all([
        api.get("/onboard/me/organization"),
        api.get("/meta/agency-types"),
        api.get("/meta/agency-roles"),
      ]);
      setOrg(orgResp.organization);
      setMeta(t);
      setRoles(r);
    })();
  }, []);

  const pickedTypeKeys = org?.agency_types || [];
  // Union of every desk unlocked by picked types
  const conditionalDeskKeys = useMemo(() => {
    if (!meta) return [];
    const s = new Set();
    for (const k of pickedTypeKeys) {
      (meta.type_desks[k] || []).forEach((d) => s.add(d));
    }
    return [...s];
  }, [pickedTypeKeys.join("|"), meta]);

  if (!org || !meta || !roles) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55">
        Loading agency office…
      </div>
    );
  }
  if (org.type !== "advertising_agency") {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="cs-card rounded-2xl p-10 text-center">
          <Megaphone size={32} className="mx-auto text-white/40 mb-4" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Agency Office</h1>
          <p className="mt-3 text-sm text-white/65">
            Your org is registered as &ldquo;{org.type}&rdquo;, not an advertising agency.
          </p>
        </div>
      </div>
    );
  }

  const pickedTypes = meta.types.filter((t) => pickedTypeKeys.includes(t.key));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="agency-office-dashboard">

      {/* HERO */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        L2 · Advertising Agency
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
          data-testid="agency-office-brand">
        {org.brand_name}
      </h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        <span>{org.legal_name}</span>
        {org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
            <BadgeCheck size={10} className="inline mr-1" /> Verified
          </span>
        )}
        {typeof org.office_geofence_radius_m === "number" && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65"
                data-testid="agency-office-geofence-chip">
            <MapPin size={10} className="inline mr-1" /> Office radius · {org.office_geofence_radius_m}m
          </span>
        )}
      </div>

      {/* SELECTED TYPES CHIPS */}
      {pickedTypes.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="agency-office-picked-types">
          {pickedTypes.map((t) => {
            const Icon = Icons[t.icon] || Icons.Circle;
            return (
              <span key={t.key}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] tracking-[0.15em] uppercase font-semibold"
                    style={{ background: `${t.accent}22`, color: t.accent }}
                    data-testid={`agency-office-type-chip-${t.key}`}>
                <Icon size={12} /> {t.label}
              </span>
            );
          })}
        </div>
      )}

      {/* CO-OWNERS SUMMARY */}
      {Array.isArray(org.co_owners) && org.co_owners.length > 0 && (
        <div className="mt-6 rounded-xl border border-white/12 bg-white/[0.02] p-4"
             data-testid="agency-office-co-owners">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
            Co-Owners · {org.co_owners.length}
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {org.co_owners.map((c, i) => (
              <div key={i}
                   className="flex items-center justify-between rounded-lg border border-white/10 bg-black/25 px-3 py-2"
                   data-testid={`agency-office-co-owner-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white/85 truncate">{c.name || c.email}</div>
                  <div className="text-[11px] text-white/45 truncate">
                    {c.email}{c.phone ? ` · ${c.phone}` : ""}
                  </div>
                </div>
                <span className={
                  "px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase font-semibold " +
                  (c.invite_status === "accepted"
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "bg-amber-500/15 text-amber-300")
                }>
                  {c.invite_status || "pending"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ALWAYS-ON DESKS */}
      <SectionHeader
        title="Always-On Desks"
        subtitle="Every agency runs these. Live for all types."
        testid="agency-office-always-on-heading"
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4"
           data-testid="agency-office-attendance-tile-wrap">
        <Link to="/office/attendance"
              className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block group"
              data-testid="agency-office-attendance-tile">
          <div className="flex items-start justify-between gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
              <Icons.Fingerprint size={18} />
            </div>
            <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase bg-emerald-500/20 text-emerald-200 font-semibold">
              Live
            </span>
          </div>
          <div className="mt-4">
            <div className="font-display text-lg uppercase tracking-tight">Attendance</div>
            <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">
              Punch in/out · team dashboard · leave requests · geofence + IP config.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
              Open →
            </span>
            <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition" />
          </div>
        </Link>

        <Link to="/office/headcount"
              className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block group"
              data-testid="agency-office-headcount-tile">
          <div className="flex items-start justify-between gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center">
              <Icons.Users size={18} />
            </div>
            <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase bg-indigo-500/20 text-indigo-200 font-semibold">
              HR
            </span>
          </div>
          <div className="mt-4">
            <div className="font-display text-lg uppercase tracking-tight">Headcount</div>
            <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">
              Promote, transfer, deactivate staff · with full audit history.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
              Open →
            </span>
            <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition" />
          </div>
        </Link>

        <Link to="/office/public-profile"
              className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block group"
              data-testid="agency-office-public-profile-tile">
          <div className="flex items-start justify-between gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-300 flex items-center justify-center">
              <Icons.Globe size={18} />
            </div>
            <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase bg-amber-500/20 text-amber-200 font-semibold">
              Public
            </span>
          </div>
          <div className="mt-4">
            <div className="font-display text-lg uppercase tracking-tight">Agency Profile</div>
            <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">
              Your @handle page — statement, hero, showreel, creator/site roster.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
              Open →
            </span>
            <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition" />
          </div>
        </Link>

        <Link to="/me/public-profile"
              className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block group"
              data-testid="agency-office-my-profile-tile">
          <div className="flex items-start justify-between gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/15 text-violet-300 flex items-center justify-center">
              <Icons.UserCircle2 size={18} />
            </div>
            <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase bg-violet-500/20 text-violet-200 font-semibold">
              Me
            </span>
          </div>
          <div className="mt-4">
            <div className="font-display text-lg uppercase tracking-tight">My Profile</div>
            <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">
              Your /u/@handle · bio, availability, showreel, work history.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
              Open →
            </span>
            <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition" />
          </div>
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
           data-testid="agency-office-always-on-grid">
        {meta.always_on_desks.map((k) => (
          <DeskCard key={k} deskKey={k} deskLabels={meta.desk_labels} alwaysOn />
        ))}
      </div>

      {/* Parked · Retainer + Variable Costs (roadmap placeholder) */}
      <div className="mt-4 rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-4 flex items-center gap-3 flex-wrap"
           data-testid="agency-office-parked-retainer">
        <div className="w-10 h-10 rounded-lg bg-white/6 flex items-center justify-center text-white/50">
          <Icons.Wallet size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
            Roadmap · not built yet
          </div>
          <div className="mt-0.5 font-display text-base uppercase tracking-tight text-white/85">
            Retainer + Variable Costs
          </div>
          <p className="mt-1 text-[11.5px] text-white/50 max-w-xl">
            Track fixed retainer per client + variable costs (which resource used how much time).
            Complicated. Parked for after beta feedback. Drop a note via the feedback bubble if this is critical for you.
          </p>
        </div>
        <span className="text-[10px] tracking-[0.2em] uppercase text-white/35">Phase 2/3</span>
      </div>

      {/* CONDITIONAL DESKS */}
      <SectionHeader
        title={`Type-Specific Desks (${conditionalDeskKeys.length})`}
        subtitle="Lit up by the agency types you picked at signup."
        testid="agency-office-conditional-heading"
      />
      {conditionalDeskKeys.length === 0 ? (
        <div className="cs-card rounded-2xl p-8 text-center text-sm text-white/55">
          No agency types on file yet. Pick at least one during onboarding
          to unlock type-specific desks.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="agency-office-conditional-grid">
          {conditionalDeskKeys.map((k) => (
            <DeskCard key={k} deskKey={k} deskLabels={meta.desk_labels} />
          ))}
        </div>
      )}

      {/* STAFFING */}
      <SectionHeader
        title="Staffing"
        subtitle="Grouped by function. Copywriters & Art Directors carry a 4-tier ladder."
        testid="agency-office-staffing-heading"
      />
      <StaffingPanel roles={roles} />

      {/* CONSULT */}
      <SectionHeader
        title="Consult"
        subtitle="Search Casting & PR directories directly from your office."
        testid="agency-office-consult-heading"
      />
      <ConsultPanel />

      {/* TEAM */}
      <SectionHeader
        title="Team"
        subtitle="Invite partners, producers, planners, servicing execs via WhatsApp."
        testid="agency-office-team-heading"
      />
      <OrgTeamPanel org={org} />

      <FloatingFeedbackBubble />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function SectionHeader({ title, subtitle, testid }) {
  return (
    <div className="mt-12 mb-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        {subtitle}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight mt-1">
        {title}
      </h2>
    </div>
  );
}

const DESK_ICON = {
  client_roster:     Users2,
  brief_locker:      FileText,
  delivery_bills:    Receipt,
  creative_room:     Icons.Palette,
  bid_bay:           Icons.Gavel,
  ppm_room:          Icons.CalendarClock,
  on_set_rep:        Icons.Clapperboard,
  post_suite:        Icons.Video,
  consult:           Search,
  site_inventory:    Icons.MapPinned,
  vendor_roster:     Icons.Truck,
  site_verification: Icons.CheckSquare,
  media_compliance:  Icons.ShieldCheck,
  campaign_manager:  Icons.Target,
  performance_desk:  Icons.LineChart,
  community_desk:    Icons.MessagesSquare,
  media_plan:        Icons.ClipboardList,
  vendor_inventory:  Icons.PackageOpen,
  slot_booking:      Icons.CalendarCheck,
  reconciliation:    Icons.Scale,
  creator_roster:    Icons.Users,
  campaign_room:     Icons.Sparkles,
  content_vault:     Icons.FolderKanban,
  creator_payments:  Icons.CircleDollarSign,
  event_ops:         Icons.PartyPopper,
};

const DESK_KEY_TO_KIND = {
  client_roster:    "client",
  brief_locker:     "brief",
  delivery_bills:   "invoice",
  bid_bay:          "bid",
  ppm_room:         "ppm",
  campaign_manager: "campaign",
  content_studio:   "content",
  creator_roster:   "creator",
  site_inventory:   "site",
};

function DeskCard({ deskKey, deskLabels, alwaysOn = false }) {
  const meta = deskLabels?.[deskKey] || { label: deskKey, blurb: "" };
  const Icon = DESK_ICON[deskKey] || Briefcase;
  const kind = DESK_KEY_TO_KIND[deskKey];
  const clickable = !!kind;
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/8 flex items-center justify-center text-white/85">
          <Icon size={18} />
        </div>
        {alwaysOn && (
          <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase bg-emerald-500/15 text-emerald-300 font-semibold">
            Always on
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="font-display text-lg uppercase tracking-tight">
          {meta.label}
        </div>
        <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed min-h-[36px]">
          {meta.blurb}
        </p>
      </div>
      <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.22em] uppercase text-white/40">
          {clickable ? "Open →" : "Coming soon"}
        </span>
        <ArrowRight size={13} className={clickable ? "text-white/60" : "text-white/25"} />
      </div>
    </>
  );
  const cls = "cs-card rounded-xl p-5 hover:border-white/25 transition-all block";
  return clickable
    ? <Link to={`/office/desks/${kind}`} className={cls}
            data-testid={`agency-desk-card-${deskKey}`}>{inner}</Link>
    : <div className={cls} data-testid={`agency-desk-card-${deskKey}`}>{inner}</div>;
}

/* ---- Staffing ------------------------------------------------------------ */

const LADDER_TIERS = ["Trainee", "Junior", "Mid", "Senior"];

function StaffingPanel({ roles }) {
  const [open, setOpen] = useState(() => new Set(["leadership", "creative"]));
  const toggle = (k) => {
    setOpen((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  };
  const ladderRoles = roles.ladder_roles || {};
  // Keys that live inside a 4-tier ladder — collapsed into a single row
  // per ladder in the UI (Copywriter · Art Director).
  const ladderMemberKeys = new Set(
    Object.values(ladderRoles).flat()
  );

  return (
    <div className="cs-card rounded-2xl p-5" data-testid="agency-staffing-panel">
      {roles.groups.map((g) => {
        const isOpen = open.has(g.key);
        // Ladders relevant to THIS group only (creative). Empty on others.
        const relevantLadders = Object.entries(ladderRoles).filter(([, keys]) =>
          keys.some((k) => g.roles.find((r) => r.key === k))
        );
        // Non-ladder roles rendered as regular chips
        const chipRoles = g.roles.filter((r) => !ladderMemberKeys.has(r.key));

        return (
          <div key={g.key}
               className="border-b border-white/8 last:border-b-0 py-3">
            <button
              type="button"
              onClick={() => toggle(g.key)}
              className="w-full flex items-center justify-between gap-3 py-2"
              data-testid={`agency-staffing-group-${g.key}`}
            >
              <div className="flex items-center gap-2">
                {isOpen
                  ? <ChevronDown size={15} className="text-white/60" />
                  : <ChevronRight size={15} className="text-white/60" />}
                <span className="font-display text-base uppercase tracking-tight">
                  {g.label}
                </span>
                <span className="text-[10px] tracking-[0.2em] uppercase text-white/40">
                  · {g.roles.length}
                </span>
              </div>
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/35">
                {isOpen ? "Hide" : "Show"}
              </span>
            </button>

            {isOpen && (
              <div className="mt-3 space-y-3">
                {/* 4-tier ladders (Copywriter · Art Director) */}
                {relevantLadders.map(([root, keys]) => (
                  <div key={root}
                       className="rounded-lg border border-amber-400/25 bg-amber-400/5 px-3 py-2.5"
                       data-testid={`agency-ladder-${root}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-[11px] tracking-[0.22em] uppercase text-amber-200 font-semibold">
                          {root.replace(/_/g, " ")} · Ladder
                        </div>
                        <div className="mt-0.5 text-[10px] text-white/45">
                          Trainee → Junior → Mid → Senior
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {keys.map((rk, i) => (
                          <span key={rk}
                                className="px-2 py-1 rounded-full text-[10px] tracking-[0.12em] uppercase bg-white/8 text-white/80"
                                data-testid={`agency-ladder-tier-${root}-${i}`}>
                            {LADDER_TIERS[i]}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Non-ladder role chips */}
                {chipRoles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {chipRoles.map((r) => (
                      <span key={r.key}
                            className="px-2.5 py-1 rounded-full text-[10.5px] bg-white/6 text-white/75 border border-white/8"
                            data-testid={`agency-role-chip-${r.key}`}>
                        {r.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="mt-4 pt-3 border-t border-white/8 text-[11px] text-white/45 leading-relaxed">
        <Layers size={11} className="inline mr-1 text-white/55" />
        Phase 2 wires this into the live headcount tracker — hire, promote,
        and shift staff between desks with role changes.
      </div>
    </div>
  );
}

/* ---- Consult ------------------------------------------------------------- */

function ConsultPanel() {
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="agency-consult-panel">
      <Link to="/directory/orgs?type=casting_agency"
            className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block"
            data-testid="agency-consult-casting">
        <div className="w-10 h-10 rounded-lg bg-violet-500/15 text-violet-300 flex items-center justify-center">
          <Users2 size={18} />
        </div>
        <div className="mt-4 font-display text-lg uppercase tracking-tight">Casting Directory</div>
        <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">
          Browse verified Casting agencies · shortlists for talent & models.
        </p>
        <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
            Open directory
          </span>
          <ArrowRight size={13} className="text-white/40" />
        </div>
      </Link>
      <Link to="/directory/orgs?type=pr_agency"
            className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block"
            data-testid="agency-consult-pr">
        <div className="w-10 h-10 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center">
          <Megaphone size={18} />
        </div>
        <div className="mt-4 font-display text-lg uppercase tracking-tight">PR Directory</div>
        <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">
          Reach out to PR shops for launches, press releases, sponsor deals.
        </p>
        <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
            Open directory
          </span>
          <ArrowRight size={13} className="text-white/40" />
        </div>
      </Link>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Floating thought-bubble — every logged-in user can drop feedback about the
   office UX. Lands in db.office_feedback for HR review.                       */
function FloatingFeedbackBubble() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState("");

  const send = async () => {
    if (msg.trim().length < 3) { toast.error("Add a note first"); return; }
    setBusy(true);
    try {
      await api.post("/office/feedback", {
        message: msg,
        context: `AgencyOffice · ${window.location.pathname}`,
      });
      toast.success("Thanks — HR will see this");
      setMsg("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen((s) => !s)}
        aria-label="Feedback"
        className="fixed bottom-6 left-6 z-50 rounded-full bg-white text-black shadow-xl hover:scale-105 transition inline-flex items-center justify-center"
        style={{ width: 52, height: 52 }}
        data-testid="feedback-bubble-btn"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>

      {open && (
        <div className="fixed bottom-24 left-6 z-50 w-[92vw] max-w-sm cs-card rounded-2xl p-5"
             data-testid="feedback-bubble-panel">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-amber-300" />
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">
              Thought bubble
            </div>
          </div>
          <div className="font-display text-lg uppercase tracking-tight">
            What&apos;s on your mind?
          </div>
          <p className="mt-1 text-[11.5px] text-white/50 leading-relaxed">
            Feature idea, bug, UX rough edge — send it straight to your HR / admin queue.
          </p>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)}
            maxLength={1000}
            placeholder="Type your feedback…"
            className="mt-3 w-full h-24 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none"
            data-testid="feedback-bubble-input" />
          <div className="mt-3 flex justify-end">
            <button onClick={send} disabled={busy}
              className="h-9 px-4 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="feedback-bubble-submit">
              <Send size={12} /> {busy ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

